import {useDispatch, useSelector} from 'react-redux'
import {useEffect, useRef, useState} from 'react'

import {AppState} from '../../../../store'
import {
   LoanCalculatorSlidersTypes,
   UseLoanOfferSummaryPanelLogicProps,
   UseLoanOfferSummaryPanelLogicReturnProps
} from '../LoanOfferSummaryPanelProps'
import {ICalculateLoanParams, loanInitService} from '../../../../service/LoanInitService'
import {SET_LOAN_CALCULATE_PARAMS, TOGGLE_CALCULATOR_OPEN} from '../../../../store/actions/loan-calculator-actions'
import { PREDEFINED_PERIODS_OFFER_LOGIC } from '../../../../store/types'
import { Environment } from '../../../../environment/app-settings'
import { SET_CURRENT_SELECTED_PREDEFINED_PERIOD } from '../../../../store/actions/application-actions'
import { sleep } from '../../../../common/helpers/helpers'


/**
 *
 * @param props Hook obsługujący logikę panelu podsumowania oferty pożyczkowej we wszystkich wersjach
 * @returns
 */
export function useLoanOfferSummaryPanelLogic(props: UseLoanOfferSummaryPanelLogicProps): UseLoanOfferSummaryPanelLogicReturnProps {
   const dispatch = useDispatch()
   const { loanVariant, defaultOpen } = props
   const debounceRef = useRef<NodeJS.Timeout | null>(null);
   const abortRef = useRef<AbortController | null>(null);

   const isLoanCalculatorOpen: boolean = useSelector((state: AppState) => state.loanCalculatorState.isCalculatorOpen)
   const { requestedAmount, instalments, instalmentAmount } = useSelector((state: AppState) => loanVariant === 'predefined' ? state.loanCalculatorState : state.offertState)
   const { IS_PREDEFINED_PERIODS_AVAILABLE, currentSelectedPredefinedPeriod } = useSelector((state: AppState) => state.applicationState)

   // LOGIKA BIZNESOWA - Jeśli kalkulator jest w trybie tylko do odczytu i wybrano wariant 'selectedOffer', korzystamy z oferty wybranej przez użytkownika, w innym przypadku z predefiniowanej
   const loanCalculatorStateParams = useSelector((state: AppState) => state.loanCalculatorState)

   const [isCurrentlyOpen, setIsCurrentlyOpen] = useState<boolean>(isLoanCalculatorOpen)
   const [isLoading, setIsLoading] = useState<boolean>(false)

   const onValueChanged = () =>{
      setIsLoading(true)
   }

   useEffect(() => {
      setIsCurrentlyOpen(defaultOpen === undefined ? isLoanCalculatorOpen : defaultOpen)
   }, [])


   /**
    * Funkcja pozwalająca na globalne sterowanie tym czy kalkulator jest otwarty czy zamknięty
    * @param isOpen
   */
   const setLoanCalculatorOpen = (isOpen: boolean) => {
      setIsCurrentlyOpen(isOpen)

      dispatch({
         type: TOGGLE_CALCULATOR_OPEN,
         payload: isOpen
      })
   }


   const handleOnPredefinedPeriodButtonClick = async (newSelectedPeriod: PREDEFINED_PERIODS_OFFER_LOGIC) => {
      if (abortRef.current) {
         abortRef.current.abort()
      }

      const controller = new AbortController()
      abortRef.current = controller

      try {
         if(currentSelectedPredefinedPeriod === newSelectedPeriod) { // Zapobiega spamowaniu przycisków z okresami
            return
         }

         setIsLoading(true)
         await sleep(400) // Debounce na wypadek szybkiego klikania w przyciski wyboru okresu

         const isShortPeriodRequestedAmountLimitReached = newSelectedPeriod === PREDEFINED_PERIODS_OFFER_LOGIC.SHORT_PERIOD && requestedAmount > Environment.maxShortLoanPeriodRequestedAmount
         const instalmentsBasedOnPredefinedPeriod = Environment.PREDEFINED_PERIODS_OFFER_LOGIC_INSTALMENT_MAP[newSelectedPeriod]

         const recalculateLoanOfferParams: ICalculateLoanParams = {
            instalments: instalmentsBasedOnPredefinedPeriod,
            requestedAmount: isShortPeriodRequestedAmountLimitReached ? Environment.maxShortLoanPeriodRequestedAmount : requestedAmount,
         }

         const response = await loanInitService.calculateLoanOffert(recalculateLoanOfferParams, controller.signal)
         
         dispatch({
            type: SET_LOAN_CALCULATE_PARAMS, 
            payload: response
         })

         dispatch({
            type: SET_CURRENT_SELECTED_PREDEFINED_PERIOD,
            payload: newSelectedPeriod
         })   
         
      }
      catch (err: any) {
         if (err.name !== "AbortError") {
            console.error(err);
         }
      }
      finally {
         setIsLoading(false)
      }
   }


   // Funkcja zwracająca odpowiedź z przeliczenia oferty pożyczkowej w zależności od typu suwaka
   const getRecalculateLoanOfferResponse = async(newSliderValue: number, type: LoanCalculatorSlidersTypes, signal?: AbortSignal) => {
      setIsLoading(true)
      await sleep(500) // Sztucznie przytrzymujemy loader, w razie gdy zapytanie wykona się zbyt szybko

      switch(type) {
         case LoanCalculatorSlidersTypes.amount: {
            let recalculateLoanOfferParams: ICalculateLoanParams = {
               instalments: loanCalculatorStateParams.instalments, // Obecna liczba rat
               requestedAmount: newSliderValue, // Nowa kwota wnioskowana
            }

            // [ZAŁOŻENIE BIZNESOWE] - jeśli klient ma dostępną opcję wyboru rat, w momencie gdy ma wybraną niższą opcję i przekroczy zadany pułap kwoty, to okres spłaty automatycznie zmieni się na dłuższy
            if(IS_PREDEFINED_PERIODS_AVAILABLE === true && newSliderValue > Environment.maxShortLoanPeriodRequestedAmount) {
               const newSelectedPeriod = Environment.PREDEFINED_PERIODS_OFFER_LOGIC_INSTALMENT_MAP[PREDEFINED_PERIODS_OFFER_LOGIC.LONG_PERIOD]
               recalculateLoanOfferParams.instalments = newSelectedPeriod

               dispatch({
                  type: SET_CURRENT_SELECTED_PREDEFINED_PERIOD,
                  payload: PREDEFINED_PERIODS_OFFER_LOGIC.LONG_PERIOD
               })
            }

            try {
               return await loanInitService.calculateLoanOffert(recalculateLoanOfferParams, signal)
            }
            finally {
               setIsLoading(false)
            }
         }
         case LoanCalculatorSlidersTypes.period: {
            const recalculateLoanOfferParams: ICalculateLoanParams = {
               instalments: newSliderValue, // Nowa liczba rat
               requestedAmount: loanCalculatorStateParams.requestedAmount, // Obecna kwota wnioskowana
            }
            try {
               return await loanInitService.calculateLoanOffert(recalculateLoanOfferParams)
            }
            finally {
               setIsLoading(false)
            }
         }
      }
   }

   const noOfCalculation = useSelector(
       (state: AppState) => state.loanCalculatorState.noOfCalculation
   );

   // Funkcja obsługująca zmianę wartości suwaka w kalkulatorze pożyczkowym
   const onLoanValueSliderUpdate =  async(newSliderValue: number | number[], type: LoanCalculatorSlidersTypes) => {
      if (debounceRef.current){
         clearTimeout(debounceRef.current);
      }

      const DEFAULT_MIN_DEBOUNCE_TIMEOUT = 250
      const DEFAULT_MAX_DEBOUNCE_TIMEOUT = 1000
      const DEFAULT_STEP_DEBOUNCE_TIMEOUT = 150

      const delay = Math.min(
          DEFAULT_MAX_DEBOUNCE_TIMEOUT,
          DEFAULT_MIN_DEBOUNCE_TIMEOUT + (noOfCalculation - 1) * DEFAULT_STEP_DEBOUNCE_TIMEOUT
      );

      
      return new Promise<void>((resolve, reject) => {
         debounceRef.current = setTimeout(async () => {
            try {
               await sendRecalculateRequest(newSliderValue as number, type)
               resolve()
            }
            catch(error) {
               reject(error)
            }
         }, delay)
      })
   }


   const sendRecalculateRequest = async (value: number, type: LoanCalculatorSlidersTypes) => {
      if (abortRef.current) {
         abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;
      try {
         const response = await getRecalculateLoanOfferResponse(value, type, controller.signal);
         dispatch({type: SET_LOAN_CALCULATE_PARAMS, payload: response});
      } 
      catch (err) {
         // @ts-ignore
         if (err.name !== "AbortError") {
            console.error(err);
         }
      }
   };





   return {
      loanShortSummaryData: { requestedAmount, instalments, instalmentAmount },
      loanCalculatorStateParams,
      isLoanCalculatorOpen: isCurrentlyOpen,
      isLoading: isLoading,
      handleOnPredefinedPeriodButtonClick,
      setLoanCalculatorOpen,
      onLoanValueSliderUpdate,
      onValueChanged: onValueChanged
   }
}